import { getAuthHeaders } from "@/lib/auth-fetch";
import { getAuthSession } from "@/lib/auth-session";
import { API_BASE } from "@/lib/utils";

async function platformFetch(path, options = {}) {
  const headers = {
    "content-type": "application/json",
    ...getAuthHeaders(),
    ...(options.headers || {}),
  };
  if (!headers.Authorization) {
    throw new Error("Not signed in — sign out and log in again at /login as ops@aivoryems.com");
  }
  const res = await fetch(`${API_BASE}/api/platform${path}`, {
    ...options,
    headers,
    credentials: "same-origin",
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 401 || res.status === 403) {
    throw new Error(body?.detail || "Session expired — sign out and log in again");
  }
  if (!res.ok) {
    throw new Error(body?.detail || `Request failed (${res.status})`);
  }
  return body;
}

export function fetchPlatformSummary() {
  return platformFetch("/summary");
}

export function fetchAccounts() {
  return platformFetch("/accounts");
}

export function startTrial(payload) {
  return platformFetch("/trials", { method: "POST", body: JSON.stringify(payload) });
}

export function inviteTrial(payload) {
  return platformFetch("/trials/invite", { method: "POST", body: JSON.stringify(payload) });
}

export function fetchTrialInvites(limit = 50) {
  return platformFetch(`/trial-invites?limit=${encodeURIComponent(limit)}`);
}

export function extendTrial(orgId, extraDays = 7) {
  return platformFetch(`/accounts/${encodeURIComponent(orgId)}/extend-trial`, {
    method: "POST",
    body: JSON.stringify({ extraDays }),
  });
}

export function fetchLeads(stage, source) {
  const params = new URLSearchParams();
  if (stage) params.set("stage", stage);
  if (source) params.set("source", source);
  const q = params.toString() ? `?${params.toString()}` : "";
  return platformFetch(`/leads${q}`);
}

export function createLead(payload) {
  return platformFetch("/leads", { method: "POST", body: JSON.stringify(payload) });
}

export function updateLead(leadId, payload) {
  return platformFetch(`/leads/${encodeURIComponent(leadId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function fetchPlatformDemoEvent() {
  return platformFetch("/demo-event");
}

export function updatePlatformDemoEvent(content) {
  return platformFetch("/demo-event", {
    method: "PATCH",
    body: JSON.stringify({ content }),
  });
}

export function fetchPlatformLanding() {
  return platformFetch("/landing");
}

export function updatePlatformLanding(content) {
  return platformFetch("/landing", {
    method: "PATCH",
    body: JSON.stringify({ content }),
  });
}

export function fetchBillingRequests(status = "submitted") {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return platformFetch(`/billing/requests${q}`);
}

export function approveBillingRequest(requestId, reviewNotes = "") {
  return platformFetch(`/billing/requests/${encodeURIComponent(requestId)}/approve`, {
    method: "POST",
    body: JSON.stringify({ reviewNotes }),
  });
}

export function rejectBillingRequest(requestId, reviewNotes = "") {
  return platformFetch(`/billing/requests/${encodeURIComponent(requestId)}/reject`, {
    method: "POST",
    body: JSON.stringify({ reviewNotes }),
  });
}

export function fetchPlatformStatus() {
  return platformFetch("/status");
}

export function upsertPlatformMaintenance(payload) {
  return platformFetch("/status/maintenance", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function deletePlatformMaintenance(noticeId) {
  return platformFetch(`/status/maintenance/${encodeURIComponent(noticeId)}`, {
    method: "DELETE",
  });
}

export function fetchBillingConfig() {
  return platformFetch("/billing/config");
}

export function updateBillingConfig(payload) {
  return platformFetch("/billing/config", { method: "PATCH", body: JSON.stringify(payload) });
}

export function registerRevolutWebhook() {
  return platformFetch("/billing/revolut/register-webhook", { method: "POST" });
}

export function syncRevolutSubscriptionPlans() {
  return platformFetch("/billing/revolut/sync-plans", { method: "POST" });
}

export function fetchAccountDetail(orgId) {
  return platformFetch(`/accounts/${encodeURIComponent(orgId)}/detail`);
}

export function createPlatformInvoice(orgId, payload) {
  return platformFetch(`/accounts/${encodeURIComponent(orgId)}/invoices`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function issuePlatformInvoice(orgId, invoiceId) {
  return platformFetch(`/accounts/${encodeURIComponent(orgId)}/invoices/${encodeURIComponent(invoiceId)}/issue`, {
    method: "POST",
  });
}

export function markInvoicePaid(orgId, invoiceId) {
  return platformFetch(`/accounts/${encodeURIComponent(orgId)}/invoices/${encodeURIComponent(invoiceId)}/paid`, {
    method: "POST",
  });
}

export function setAccountPlan(orgId, plan) {
  return platformFetch(`/accounts/${encodeURIComponent(orgId)}/plan`, {
    method: "POST",
    body: JSON.stringify({ plan }),
  });
}

export function setAccountStatus(orgId, status, reason = "") {
  return platformFetch(`/accounts/${encodeURIComponent(orgId)}/status`, {
    method: "POST",
    body: JSON.stringify({ status, reason }),
  });
}

export function impersonateTenant(orgId, readOnly = true) {
  return platformFetch("/impersonate", {
    method: "POST",
    body: JSON.stringify({ orgId, readOnly }),
  });
}

export function fetchPlatformAudit(limit = 200) {
  return platformFetch(`/audit?limit=${encodeURIComponent(String(limit))}`);
}

export function runBillingJobs() {
  return platformFetch("/billing/jobs/run", { method: "POST" });
}

export function isPlatformSession() {
  const session = getAuthSession();
  return session?.role === "platform_admin";
}
